// src/contexts/DocumentContext.js
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { fetchWithCache } from '../utils/cacheUtils';
import { useLogto } from '@logto/react';
import useApi from '../hooks/useApi';

const DocumentContext = createContext({});

export const DocumentProvider = ({ children }) => {
    const { isAuthenticated } = useLogto();
    const api = useApi();
    const apiRef = useRef(api);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Keep apiRef updated with latest api instance
    useEffect(() => {
        apiRef.current = api;
    }, [api]);

    const fetchDocumentTypes = useCallback(async () => {
        setLoading(true);
        try {
            const getDocumentTypes = () => apiRef.current.get('/api/v1/document/types');
            const data = await fetchWithCache(getDocumentTypes, 'documentTypes');
            setDocumentTypes(data);
        } catch (error) {
            console.error('Failed to fetch document types (context):', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchDocumentTypes();
        } else {
            setDocumentTypes([]);
            setLoading(false);
        }
    }, [isAuthenticated, fetchDocumentTypes]);

    const invalidateCacheAndRefresh = useCallback(() => {
        sessionStorage.removeItem('documentTypes');
        sessionStorage.removeItem('documentTypesFetchTime');
        fetchDocumentTypes();
    }, [fetchDocumentTypes]);

    const addDocumentType = async (newTypeName) => {
        try {
            await apiRef.current.post('/api/v1/document/types', { name: newTypeName });
            invalidateCacheAndRefresh();
        } catch (error) {
            console.error('Failed to add document type:', error);
        }
    };

    const editDocumentType = async (id, updatedName) => {
        try {
            await apiRef.current.put(`/api/v1/document/types/${id}`, { name: updatedName });
            invalidateCacheAndRefresh();
        } catch (error) {
            console.error('Failed to edit document type:', error);
        }
    };

    const deleteDocumentType = async (id) => {
        try {
            await apiRef.current.delete(`/api/v1/document/types/${id}`);
            invalidateCacheAndRefresh();
        } catch (error) {
            console.error('Failed to delete document type:', error);
        }
    };

    const value = {
        documentTypes,
        addDocumentType,
        editDocumentType,
        deleteDocumentType,
        refreshDocumentTypes: fetchDocumentTypes,
        loading
    };

    return (
        <DocumentContext.Provider value={value}>
            {children}
        </DocumentContext.Provider>
    );
};

export const useDocumentContext = () => useContext(DocumentContext);
